import { API_BASE_URL } from '@/environment'
import { toQueryParams } from '@/helpers/api/toQueryParams'
import ApiService from '@/service/api.service.js'
import { customFetch } from '@/service/v3/apiV3Utils'
import type { GroupType, V3PaginatedResponseData } from '@/types'
import { queryParams } from '@attensi/utils'
import type { ApiGroup, ApiGroupType } from '../user/userTypes'
import type {
  CreateGroupByTypeParams,
  DeleteGroupParams,
  GetGroupsParams,
} from './groupsTypes'

const API_ADMIN_NAMESPACE = 'api/v3/admin'

/**
 * Returns the API endpoint path segment for a given group type
 */
const getGroupTypeEndpoint = (groupType: GroupType | ApiGroupType): string => {
  switch (groupType) {
    case 'department':
      return 'departments'
    case 'position':
      return 'positions'
    case 'custom':
    case 'default':
      return 'groups'
    case 'leaderboard':
      return 'leaderboard_groups'
  }
}

type GroupListItem<C extends boolean> = C extends true
  ? ApiGroup & { user_count: number }
  : ApiGroup

/**
 * Fetches paginated groups by type
 */
export const getGroups = async <
  T extends GroupType | ApiGroupType,
  C extends boolean = false,
>({
  companyNameKey,
  groupType,
  search = '',
  page = 1,
  pageSize = 25,
  sort = 'name',
  additional,
  includeUserCount,
}: GetGroupsParams<T, C>): V3PaginatedResponseData<GroupListItem<C>> => {
  const endpoint = getGroupTypeEndpoint(groupType)

  const groupQueryParams = {
    ...toQueryParams(
      {
        term: search || undefined,
        selected: additional === false || undefined,
        selected_additional: additional === true || undefined,
      },
      'filter'
    ),
    ...toQueryParams({ number: page, size: pageSize }, 'page'),
    sort,
    include_user_count: includeUserCount || undefined,
  }

  const url = queryParams(
    `${API_BASE_URL}/${API_ADMIN_NAMESPACE}/companies/${companyNameKey}/${endpoint}`,
    groupQueryParams
  )

  const response = await customFetch(url, { method: 'GET' })

  if (!response.ok) {
    throw new Error(
      `Failed to fetch ${groupType} groups: ${response.statusText}`
    )
  }

  return response.json()
}

export type DepartmentAttributes = {
  name: string
  parent_id: number | null
  /** Archived. `deactivate` sets this *and* re-parents the department to the
   *  root, so archived departments surface at the top level of the tree. */
  hide_from_reports: boolean
}

export type GetDepartmentsParams = {
  companyNameKey: string
  /** `null` fetches the roots. Ignored while searching, which spans the company. */
  parentId: number | null
  search?: string
  page?: number
  pageSize?: number
  /** Omit to fetch archived and active together. */
  hideFromReports?: boolean
}

/** One level of the department tree, or - with a search term - matches from
 *  anywhere in it. Children come as linkage, so a leaf needs no fetch. */
export const getDepartments = async ({
  companyNameKey,
  parentId,
  search = '',
  page = 1,
  pageSize = 50,
  hideFromReports,
}: GetDepartmentsParams): V3PaginatedResponseData<DepartmentAttributes> => {
  const scopeFilter = search
    ? { term: search }
    : parentId === null
      ? { topLevelOnly: true }
      : { parentId }

  const departmentQueryParams = {
    ...toQueryParams({ ...scopeFilter, hideFromReports }, 'filter'),
    ...toQueryParams({ number: page, size: pageSize }, 'page'),
    sort: 'name',
  }

  const url = queryParams(
    `${API_BASE_URL}/api/v3/companies/${companyNameKey}/departments`,
    departmentQueryParams
  )

  const response = await customFetch(url, { method: 'GET' })

  if (!response.ok) {
    throw new Error(`Failed to fetch departments: ${response.statusText}`)
  }

  return response.json()
}

export type AddUserToGroupParams = {
  userId: string | number
  groupId: string | number
}

/**
 * Adds a single user to a group. Single-user (not batch) so each add is an
 * independent mutation that TanStack can retry on its own — a failed user can
 * be retried without re-POSTing the ones that already succeeded.
 */
export const addUserToGroup = async ({
  userId,
  groupId,
}: AddUserToGroupParams) => {
  const body = JSON.stringify({
    data: { attributes: { user_id: userId, group_id: groupId } },
  })

  const response = await customFetch(
    `${API_BASE_URL}/${API_ADMIN_NAMESPACE}/users/${userId}/groups`,
    { method: 'POST', body }
  )

  if (!response.ok) {
    throw new Error(
      `Failed to add user ${userId} to group ${groupId}: ${response.status}`
    )
  }

  return response
}

/**
 * Group write attributes for the V3 admin API. `description` is only accepted
 * here — the V1 `/groups` endpoints (`ApiService`) silently drop it — so group
 * create/update that needs a description must go through these functions.
 * `group_type` uses the backend enum value (`'default'` for custom groups).
 */
export type GroupWriteAttributes = {
  name?: string
  description?: string | null
  group_type?: string
  external_id?: string | null
  parent_id?: number | string | null
}

export type CreateGroupParams = {
  companyNameKey: string
  attributes: GroupWriteAttributes
}

/**
 * Creates a group via the V3 admin API (JSON:API). Used for custom groups so a
 * `description` can be set at creation; other group types keep the V1 path.
 */
export const createGroup = async ({
  companyNameKey,
  attributes,
}: CreateGroupParams) => {
  const body = JSON.stringify({ data: { attributes } })

  const response = await customFetch(
    `${API_BASE_URL}/${API_ADMIN_NAMESPACE}/companies/${companyNameKey}/groups`,
    { method: 'POST', body }
  )

  if (!response.ok) {
    throw response
  }

  return response.json()
}

export const createGroupByType = async ({
  companyNameKey,
  groupType,
  name,
  description,
}: CreateGroupByTypeParams): Promise<void> => {
  if (groupType === 'default') {
    await createGroup({
      companyNameKey,
      attributes: { name, description, group_type: groupType },
    })
    return
  }

  // `ApiService` reports failure instead of throwing
  const response = await ApiService.createGroup({ name, group_type: groupType })

  if (response.error) {
    throw new Error(response.statusText ?? 'Failed to create group')
  }
}

export const deleteGroup = async ({
  groupId,
  groupType,
}: DeleteGroupParams): Promise<Response> => {
  const endpoint = getGroupTypeEndpoint(groupType)

  const response = await customFetch(
    `${API_BASE_URL}/${API_ADMIN_NAMESPACE}/${endpoint}/${groupId}`,
    { method: 'DELETE' }
  )

  if (!response.ok) {
    throw response
  }

  return response
}

export type UpdateGroupParams = {
  groupId: number | string
  attributes: GroupWriteAttributes
}

/**
 * Updates a group via the V3 admin API (JSON:API). Only editable for custom
 * groups (`group_type: "default"`) server-side. The backend fills any omitted
 * attribute from the stored record, so a description-only edit is safe to send
 * on its own.
 */
export const updateGroup = async ({
  groupId,
  attributes,
}: UpdateGroupParams) => {
  const body = JSON.stringify({ data: { attributes } })

  const response = await customFetch(
    `${API_BASE_URL}/${API_ADMIN_NAMESPACE}/groups/${groupId}`,
    { method: 'PATCH', body }
  )

  if (!response.ok) {
    throw response
  }

  return response.json()
}
